import { useState, useRef, useEffect, ChangeEvent, MouseEvent, KeyboardEvent } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { SxProps } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import BentoIcon from '../BentoIcon';

interface DatePickerFieldProps {
  id: string;
  name: string;
  label?: string;
  value: Dayjs | string | null;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  maxDate?: Dayjs | string | null;
  minDate?: Dayjs | string | null;
  size?: 'small' | 'medium' | 'large';
  helperText?: string;
  error?: boolean;
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
  slotProps?: any;
  isClearable?: boolean;
  disabled?: boolean;
  required?: boolean;
  sx?: SxProps;
}

export default function DatePickerField({
  id,
  size = 'medium',
  isClearable = true,
  value,
  onChange,
  maxDate,
  minDate,
  helperText,
  error = false,
  slotProps = {},
  onKeyPress,
  label = 'Date',
  name,
  disabled = false,
  required = false,
  sx = {},
}: DatePickerFieldProps) {
  // parse value prop to string for input and date for picker
  const getDateFromValue = (val: Dayjs | string | null) => {
    if (!val) return null;
    if (typeof val === 'string') {
      const d = dayjs(val, 'MM/DD/YYYY', true);
      return d.isValid() ? d.toDate() : null;
    }
    if (dayjs.isDayjs(val) && val.isValid()) return val.toDate();
    return null;
  };

  const getInputValueFromValue = (val: Dayjs | string | null) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (dayjs.isDayjs(val) && val.isValid()) return val.format('MM/DD/YYYY');
    return '';
  };

  const [inputValue, setInputValue] = useState(getInputValueFromValue(value));
  const [selected, setSelected] = useState<Date | null>(getDateFromValue(value));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isComposingValue, setIsComposingValue] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const shadowValue = useRef<string | null>(null);

  useEffect(() => {
    const nextInputValue = getInputValueFromValue(value);
    const nextSelected = getDateFromValue(value);

    if (!isComposingValue) {
      setInputValue((prev) => (prev !== nextInputValue ? nextInputValue : prev));
    }

    setSelected((prev) => {
      const prevTime = prev ? prev.getTime() : null;
      const nextTime = nextSelected ? nextSelected.getTime() : null;
      return prevTime !== nextTime ? nextSelected : prev;
    });
    setHasValidationError(false);
    shadowValue.current = nextInputValue || null;
  }, [value, isComposingValue]);

  // handle backspace/delete for proper character removal
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      setIsDeleting(true);
    }
  };

  // mask: MM/DD/YYYY
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9/]/g, '');
    const cursorPos = inputRef.current?.selectionStart ?? val.length;

    if (!isDeleting && (val.length === 2 || val.length === 5) && inputValue.length < val.length) {
      val += '/';
    }

    if (val.length > 10) val = val.slice(0, 10);

    // prevent full deletion when backspace at end
    if (isDeleting && cursorPos === inputValue.length && val.length < inputValue.length) {
      val = inputValue.slice(0, inputValue.length - 1);
    }

    setInputValue(val);
    setIsComposingValue(val.length > 0 && val.length < 10);
    setIsDeleting(false);

    if (val.length === 10) {
      const parsed = dayjs(val, 'MM/DD/YYYY', true);
      const isValid = parsed.isValid();
      setSelected(isValid ? parsed.toDate() : null);
      setHasValidationError(!isValid);

      if (isValid) {
        const newValue = parsed.format('MM/DD/YYYY');
        if (shadowValue.current !== newValue) {
          shadowValue.current = newValue;
          if (onChange) {
            onChange({
              target: {
                value: newValue,
                name,
              } as any,
            } as ChangeEvent<HTMLInputElement>);
          }
        }
      } else {
        if (shadowValue.current !== null) {
          shadowValue.current = null;
          if (onChange) {
            onChange({
              target: {
                value: null,
                name,
              } as any,
            } as ChangeEvent<HTMLInputElement>);
          }
        }
      }
    } else {
      setSelected(null);
      setHasValidationError(false);
      if (shadowValue.current !== null) {
        shadowValue.current = null;

        if (onChange) {
          onChange({
            target: {
              value: null,
              name,
            } as any,
          } as ChangeEvent<HTMLInputElement>);
        }
      }
      if (val.length === 0) {
        setIsComposingValue(false);
      }
    }
  };

  const handleIconClick = (event: MouseEvent<HTMLElement>) => {
    if (!disabled) setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClear = () => {
    setInputValue('');
    setSelected(null);
    setHasValidationError(false);
    setIsComposingValue(false);
    if (onChange) {
      onChange({
        target: {
          value: null,
          name,
        } as any,
      } as ChangeEvent<HTMLInputElement>);
    }
    if (inputRef.current) inputRef.current.focus();
  };

  const handleInputClick = (e: MouseEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const position = input.selectionStart ?? 0;

    if (!input.setSelectionRange) {
      return;
    }
    // MM/DD/YYYY
    // 0123456789
    if (position <= 2) {
      // select month
      input.setSelectionRange(0, 2);
    } else if (position >= 3 && position <= 5) {
      // select day
      input.setSelectionRange(3, 5);
    } else if (position >= 6) {
      // select year
      input.setSelectionRange(6, 10);
    }
  };

  // handle min/max date for daypicker
  const min = minDate ? getDateFromValue(minDate) : undefined;
  const max = maxDate ? getDateFromValue(maxDate) : undefined;

  const hasWidth = typeof sx === 'object' && sx !== null && 'width' in sx;
  const textFieldSize = size === 'large' ? 'medium' : size;

  return (
    <>
      <TextField
        id={id}
        label={label}
        name={name}
        inputRef={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onClick={handleInputClick}
        placeholder="MM/DD/YYYY"
        fullWidth={!hasWidth}
        helperText={hasValidationError ? 'Please enter a valid date' : helperText}
        error={hasValidationError || error}
        variant="outlined"
        size={textFieldSize}
        autoComplete="off"
        disabled={disabled}
        required={required}
        sx={sx}
        slotProps={{
          htmlInput: {
            ...slotProps,
            maxLength: 10,
          },
          input: {
            onKeyPress,
            endAdornment: (
              <InputAdornment position="end">
                <>
                  {isClearable && inputValue && (
                    <IconButton
                      disabled={disabled}
                      onClick={handleClear}
                      tabIndex={-1}
                      size="small"
                    >
                      <BentoIcon name="xmark" />
                    </IconButton>
                  )}
                  <IconButton
                    disabled={disabled}
                    onClick={handleIconClick}
                    tabIndex={-1}
                    size="small"
                  >
                    <EventIcon />
                  </IconButton>
                </>
              </InputAdornment>
            ),
          },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <DateCalendar
          value={selected ? dayjs(selected) : null}
          onChange={(date) => {
            setSelected(date ? date.toDate() : null);
            setHasValidationError(false);
            if (date) {
              const formatted = dayjs(date).format('MM/DD/YYYY');
              setInputValue(formatted);
              setIsComposingValue(false);
              if (onChange) {
                onChange({
                  target: {
                    value: formatted,
                    name,
                  } as any,
                } as ChangeEvent<HTMLInputElement>);
              }
            } else {
              setInputValue('');
              setIsComposingValue(false);
              if (onChange) {
                onChange({
                  target: {
                    value: null,
                    name,
                  } as any,
                } as ChangeEvent<HTMLInputElement>);
              }
            }
            setAnchorEl(null);
          }}
          minDate={min ? dayjs(min) : undefined}
          maxDate={max ? dayjs(max) : undefined}
        />
      </Popover>
    </>
  );
}
